/**
 * API Client
 * Axios-based HTTP client for API requests
 */

import axios from 'axios';
import logger from './logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Default request timeout (ms) — tuned for CRUD calls so genuine failures surface quickly.
const DEFAULT_TIMEOUT = 30000;

// Reports legitimately take longer than CRUD calls: they pull large result sets
// (limit=500–1000) and the first request after a (re)login can hit a cold tenant
// pool on Azure. Use this per-call override (config.timeout) on report endpoints
// instead of raising DEFAULT_TIMEOUT globally — see ReportsPage. A request that
// exceeds the timeout aborts client-side (ECONNABORTED) even though the API may
// still complete and log a 200, which reads to the user as a "connection error".
export const REPORT_TIMEOUT = 90000;

// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for Azure Easy Auth cookies
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Let browser set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Check for tenant in URL query parameter (takes precedence)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');

    // Add X-Tenant-ID header from URL parameter or localStorage
    if (tenantParam) {
      // Use tenant code from URL
      config.headers['X-Tenant-ID'] = tenantParam;
    } else {
      // Fall back to manually selected tenant from localStorage
      const selectedTenantId = localStorage.getItem('selectedTenantId');
      if (selectedTenantId) {
        config.headers['X-Tenant-ID'] = selectedTenantId;
      }
    }

    // For external users, attach Keycloak Bearer token
    const keycloakToken = sessionStorage.getItem('keycloakToken');
    if (keycloakToken) {
      config.headers['Authorization'] = `Bearer ${keycloakToken}`;
    }

    // In dev mode, send the logged-in user's email to the backend
    // This allows the dev auth middleware to authenticate as the correct user
    // BUT ONLY if not using Keycloak (external user authentication)
    const isDev =
      process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
    if (isDev && !keycloakToken) {
      const devUserEmail = sessionStorage.getItem('devUserEmail') || process.env.REACT_APP_DEV_USER_EMAIL;
      if (devUserEmail) {
        config.headers['x-ms-client-principal-name'] = devUserEmail;
      }
    }

    // For internal users in production, attach Azure AD access token
    const azureAccessToken = sessionStorage.getItem('azureAccessToken');
    if (azureAccessToken && !keycloakToken) {
      config.headers['Authorization'] = `Bearer ${azureAccessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Remember the server correlation id of the most recent call so the ErrorBoundary and
    // error logs can quote it for support / log correlation.
    const reqId = response.headers?.['x-request-id'];
    if (reqId) {
      try { sessionStorage.setItem('lastRequestId', reqId); } catch (e) { /* sessionStorage unavailable */ }
    }

    // Check if backend auto-switched tenant (user lost access to stored tenant)
    const switchedTenantId = response.headers['x-tenant-switched'];
    if (switchedTenantId) {
      localStorage.setItem('selectedTenantId', switchedTenantId);
      // Reload to apply new tenant context
      window.location.reload();
      return response;
    }

    // Self-heal when the backend resolved a tenant for a request that did not
    // carry an explicit X-Tenant-ID (the API only stamps X-Tenant-Resolved on
    // non-header resolution — see tenantResolver). We adopt the resolved id into
    // localStorage so every SUBSEQUENT request is explicitly scoped to it, and
    // the server keeps enforcing isolation (an unauthorized X-Tenant-ID yields a
    // 403, not a silent switch). We deliberately do NOT call
    // window.location.reload() here: a hard reload mid-workflow tears down any
    // open modal/wizard/unsaved form (the original cause of the upload-resets-modal
    // bug, and a prior reload-loop documented in Navbar.js). TenantContext remains
    // the single source of truth for the displayed tenant and reconciles on its own.
    const resolvedTenantId = response.headers['x-tenant-resolved'];
    if (resolvedTenantId) {
      const storedTenantId = localStorage.getItem('selectedTenantId');
      if (!storedTenantId) {
        // First-ever request — adopt the resolved tenant silently
        localStorage.setItem('selectedTenantId', resolvedTenantId);
      } else if (storedTenantId !== resolvedTenantId) {
        logger.warn('Tenant mismatch corrected (next request explicitly scoped, no reload)', { event: 'tenant_mismatch' });
        localStorage.setItem('selectedTenantId', resolvedTenantId);
      }
    }

    return response;
  },
  (error) => {
    const reqId = error.response?.headers?.['x-request-id'];
    if (reqId) {
      try { sessionStorage.setItem('lastRequestId', reqId); } catch (e) { /* sessionStorage unavailable */ }
    }
    // Log redacted metadata only — never error.response.data wholesale (may contain PII).
    logger.error('API error', {
      status: error.response?.status,
      url: error.config?.url,
      requestId: reqId,
      message: extractApiError(error),
    });

    // Handle specific error cases
    if (error.response?.status === 401) {
      // Note: API returns { success: false, error: { type, message } }
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || '';

      // Don't redirect on "User not found" - PermissionContext will handle this
      // by setting hasNoTenantAccess = true and routing to /no-access
      // This allows new users (authenticated via Azure AD but not in authorized_users)
      // to be shown the access request page instead of being stuck in a redirect loop
      if (errorMessage.includes('User not found') || errorMessage.includes('not found')) {
        return Promise.reject(error);
      }

      // Unauthorized - redirect to login (but not if already on login page)
      const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname === '/login/';
      if (isOnLoginPage) {
        // Already on login page, don't redirect (would cause loop)
        return Promise.reject(error);
      }

      // Loop guard: don't redirect if we already redirected within the last 5 seconds
      const lastAuthRedirect = sessionStorage.getItem('lastAuthRedirect');
      const now = Date.now();
      if (lastAuthRedirect && (now - parseInt(lastAuthRedirect, 10)) < 5000) {
        logger.warn('Auth redirect loop detected, suppressing redirect', { event: 'auth_redirect_loop' });
        return Promise.reject(error);
      }
      sessionStorage.setItem('lastAuthRedirect', now.toString());

      const isExternalUser = sessionStorage.getItem('userType') === 'external_client';
      if (isExternalUser) {
        // Clear Keycloak session and redirect to login page
        sessionStorage.removeItem('keycloakToken');
        sessionStorage.removeItem('keycloakRefreshToken');
        sessionStorage.removeItem('userType');
        sessionStorage.removeItem('externalUser');
        window.location.href = '/login';
      } else {
        // Redirect to login page - user clicks "Sign in with Microsoft"
        window.location.href = '/login';
      }
    } else if (error.response?.status === 403) {
      const errorCode = error.response?.data?.error;

      // If tenant access was denied, let PermissionContext handle the state
      // Do NOT redirect here to avoid redirect loops
      if (errorCode === 'tenant_access_denied' || errorCode === 'no_tenant_access') {
        logger.warn('Tenant access denied - propagating to PermissionContext', { event: 'tenant_access_denied' });
        localStorage.removeItem('selectedTenantId');
        // Let the error propagate - PermissionContext will catch it and set hasNoTenantAccess=true
        // App.js will then route to /no-access page
      }
      // Other 403s already logged above as 'API error'.
    }

    return Promise.reject(error);
  }
);

/**
 * GET request
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiGet = async (url, config = {}) => {
  try {
    const response = await apiClient.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * POST request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiPost = async (url, data, config = {}) => {
  try {
    const response = await apiClient.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * PUT request
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiPut = async (url, data, config = {}) => {
  try {
    const response = await apiClient.put(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * DELETE request
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiDelete = async (url, config = {}) => {
  try {
    const response = await apiClient.delete(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload file (multipart/form-data)
 * @param {string} url - API endpoint
 * @param {FormData} formData - Form data with file
 * @param {Object} config - Axios config
 * @returns {Promise} Response data
 */
export const apiUpload = async (url, formData, config = {}) => {
  try {
    // Do NOT set Content-Type here. The request interceptor strips it for
    // FormData so the browser can set `multipart/form-data; boundary=...`
    // itself — a hard-coded value would omit the boundary and break parsing.
    const response = await apiClient.post(url, formData, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Download a binary file via POST (e.g. xlsx export)
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<Blob>} Response blob
 */
export const apiDownload = async (url, data, config = {}) => {
  try {
    const response = await apiClient.post(url, data, { ...config, responseType: 'blob' });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Extract data from API response wrapper.
 * Handles the various response formats returned by the backend:
 *   - { success: true, data: [...] }  (standard list response)
 *   - { success: true, data: { ... } } (standard object response)
 *   - [...] (direct array)
 *   - { data: [...] } (axios-wrapped)
 *
 * @param {*} response - API response (already unwrapped by apiGet/apiPost)
 * @param {*} defaultValue - Default value if extraction fails (default: [])
 * @returns {*} The extracted data
 */
export const extractApiData = (response, defaultValue = []) => {
  if (!response) return defaultValue;
  if (response?.success && response?.data !== undefined) return response.data;
  if (response?.data !== undefined) return response.data;
  if (Array.isArray(response)) return response;
  return defaultValue;
};

/**
 * Extract error message from API error response.
 * Handles the various error formats:
 *   - err.response.data.error.message (standard API error)
 *   - err.response.data.message (simple error)
 *   - err.message (network/other error)
 *
 * @param {Error} err - Error object from catch block
 * @param {string} fallback - Fallback message
 * @returns {string} The error message
 */
export const extractApiError = (err, fallback = 'An unexpected error occurred') => {
  return err?.response?.data?.error?.message
    || err?.response?.data?.message
    || err?.message
    || fallback;
};

/**
 * Produce a user-facing message that distinguishes the *kind* of failure rather
 * than masking everything as a single generic string. The three cases a caught
 * Axios error can fall into:
 *   - timeout  : err.code === 'ECONNABORTED' (request aborted client-side after
 *                config.timeout) — the server may still be processing.
 *   - network  : no err.response at all (CORS, DNS, offline, connection refused).
 *   - server   : err.response present — surface the API's own error message.
 *
 * @param {Error} err - Error object from a catch block
 * @param {Object} [opts]
 * @param {string} [opts.action] - Short verb phrase for context, e.g. "load the report".
 * @returns {string} A specific, user-facing message.
 */
export const describeApiError = (err, { action = 'complete the request' } = {}) => {
  if (err?.code === 'ECONNABORTED') {
    return `The request took too long and timed out. The server may still be working — try a narrower date range, or retry.`;
  }
  if (!err?.response) {
    return `Lost connection to the server while trying to ${action}. Check your network and retry.`;
  }
  return extractApiError(err, `Failed to ${action}. Please try again.`);
};

/**
 * Clear selected tenant (call on logout)
 */
export const clearSelectedTenant = () => {
  localStorage.removeItem('selectedTenantId');
};

/**
 * Get currently selected tenant ID
 */
export const getSelectedTenantId = () => {
  return localStorage.getItem('selectedTenantId');
};

/**
 * Set selected tenant ID
 */
export const setSelectedTenantId = (tenantId) => {
  if (tenantId) {
    localStorage.setItem('selectedTenantId', tenantId);
  } else {
    localStorage.removeItem('selectedTenantId');
  }
};

export default apiClient;
